<template>
  <v-form ref="formRef">
    <v-container class="pa-0" fluid>
      <v-row>
        <v-col cols="12" sm="2">
          <v-text-field v-model="aufgabe.id" :class="readonly ? 'custom-readonly' : ''" :readonly="readonly" label="ID"
                        title="ID"
                        :variant="useGlobalSetting().globalTextVariant"></v-text-field>
        </v-col>
        <v-col cols="12" sm="10">
          <v-text-field v-model="aufgabe.title" :class="readonly ? 'custom-readonly' : ''" :readonly="readonly"
                        label="Titel"
                        required
                        title="Titel" :variant="useGlobalSetting().globalTextVariant"></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="12">
          <v-textarea
              v-model="aufgabe.description"
              :class="readonly ? 'custom-readonly' : ''"
              :readonly="readonly"
              label="Beschreibung"
              no-resize
              required
              title="Beschreibung"
              :variant="useGlobalSetting().globalTextVariant"
          ></v-textarea>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="d-flex flex-column" cols="12" sm="4">
          <v-text-field v-model="aufgabe.asset_id" :class="readonly ? 'custom-readonly' : ''" :readonly="readonly"
                        label="AKZ" required
                        title="AKZ" :variant="useGlobalSetting().globalTextVariant"></v-text-field>
          <GroupSelect v-model="aufgabe.work_group" :class="readonly ? 'custom-readonly' : ''"
                       :readonly="readonly" required/>
        </v-col>
        <v-col class="d-flex flex-column" cols="12" sm="4">
          <StateSelect v-model="aufgabe.state" :class="readonly ? 'custom-readonly' : ''" :readonly="readonly"
                       required :variant="useGlobalSetting().globalTextVariant"/>
          <PrioritySelect v-model="aufgabe.priority" :class="readonly ? 'custom-readonly' : ''" :readonly="readonly"
                          required :variant="useGlobalSetting().globalTextVariant"/>
        </v-col>

        <v-col cols="12" sm="4">
          <v-text-field v-model="aufgabe.assignee" :class="readonly ? 'custom-readonly' : ''" :readonly="readonly"
                        label="AGL" required
                        title="AGL" :variant="useGlobalSetting().globalTextVariant"></v-text-field>
          <TimepickerSmall v-model="aufgabe.scheduled_duration" :class="readonly ? 'custom-readonly' : ''"
                           :readonly="readonly"
                           label="Solldauer" required/>
        </v-col>
      </v-row>

      <!-- Freischaltbereich: absichtlich unabhängig von readonly –
           jeder Bearbeiter einer Freischaltung darf den Bereich zuordnen. -->
      <v-row v-if="aufgabe?.is_lockout">
        <v-col cols="12" sm="6">
          <LotoAreaSelect v-model="aufgabe.loto_area_id"
                          label="Freischaltbereich"
                          prepend-icon="mdi-map"
                          :hide-details="false"
                          density="default"
                          @update:model-value="useGlobalSetting().hasChanged = true"/>
        </v-col>
        <v-col cols="12" sm="6" class="d-flex align-center">
          <span class="text-caption text-medium-emphasis">
            Bestimmt, auf welchem Bereich des Freischaltplans diese Aufgabe erscheint.
          </span>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6">
          <v-text-field
              v-model="aufgabe.scheduled_start"
              :class="readonly ? 'custom-readonly' : ''"
              :readonly="readonly"
              label="Sollstart"
              required
              title="Sollstart"
              type="datetime-local"
              :variant="useGlobalSetting().globalTextVariant"
          ></v-text-field>
        </v-col>

        <v-col cols="12" sm="6">
          <v-text-field
              v-model="aufgabe.scheduled_end"
              :class="readonly ? 'custom-readonly' : ''"
              :readonly="readonly"
              label="Sollende"
              required
              title="Sollende"
              type="datetime-local"
              :variant="useGlobalSetting().globalTextVariant"
          ></v-text-field>
        </v-col>
      </v-row>
      <div class="mb-4" v-if="isMotorRef">
        <a
            href="#"
            @click.prevent="openMotorInformation(aufgabe?.asset_id)"
            target="_blank"
            rel="noopener"
            class="link-with-icon color_pro-rot"
        >
          <v-icon start class="no-underline">mdi-open-in-app</v-icon>
          <span class="underline-text">Zusätzliche Informationen für {{ aufgabe?.asset_id }}</span>
        </a>
      </div>
      <!--test/-->
    </v-container>
  </v-form>
</template>

<script lang="ts" setup>
import TimepickerSmall from "@/components/inputs/TimepickerSmall.vue";
import {ref, watch} from "vue";
import {Task} from "@/types/Order";
import StateSelect from "@/components/inputs/StateSelect.vue";
import PrioritySelect from "@/components/inputs/PrioritySelect.vue";
import GroupSelect from "@/components/inputs/GroupSelect.vue";
import LotoAreaSelect from "@/components/inputs/LotoAreaSelect.vue";
import {VForm} from "vuetify/components";
import {openDriveInformationDialog, useGlobalSetting} from "@/lib/utils";
import {checkMotorId} from "@/types/Drive";

const isMotorRef = ref<boolean>(false);

const formRefs = ref<InstanceType<typeof VForm>[]>([]);

interface AufgabenTab {
  icon: string;
  text: string;
  value: string;
}

const aufgabe = defineModel<Task>();

isMotor(aufgabe.value?.asset_id);

const props = defineProps({
  readonly: {
    type: Boolean,
    default: false,
  }
});

//const emit = defineEmits(["updateChangeList", "updateDeletedList"])

watch(aufgabe, (p) => {
  useGlobalSetting().hasChanged = true;
  //emit('updateChangeList', aufgabe);
});

function validateForm() {
  if (!formRefs.value.length) return Promise.resolve(false)
  Promise.all(formRefs.value.map(f => f.validate())).then((result) => {
    return result.every(r => r.valid);
  })
}

function isMotor(assetId: string | null | undefined) {
  if (!assetId) return;
  let id = String(assetId);
  if (id.toLowerCase().startsWith("spm"))
    id = id.substring(3);
  checkMotorId(id).then(data => {
    isMotorRef.value = data;
  });
}

function openMotorInformation(assetId: string | null | undefined) {
  if (!assetId) return;
  let id = String(assetId);
  let _id = id;
  if (id.toLowerCase().startsWith("spm"))
    _id = id.substring(3);
  openDriveInformationDialog(_id, false)
}

defineExpose({validateForm});
</script>
